<template>
  <div
    class="accordion-collapse">
    <div class="accordion-body p-0">
      <div class="media-props media-props--video">
        <field-group label="Position on page">
          <position-controls
              :horizontal="shownAlignment.horizontal"
              :vertical="shownAlignment.vertical"
              @align="onPositionAlign"/>
        </field-group>

        <field-group
            v-if="!isMultiPropertySelection"
            label="Rotate & flip">
          <rotate-flip-controls
              :flipped-h="!!(activeObjectForLeftPanel && activeObjectForLeftPanel.IsHFlipped)"
              :flipped-v="!!(activeObjectForLeftPanel && activeObjectForLeftPanel.IsVFlipped)"
              @rotate="(dir) => dir === 'left' ? rotateLeft() : rotateRight()"
              @flip="(dir) => dir === 'horizontal' ? flipHorizontal() : flipVertical()"/>
        </field-group>

        <field-group
            v-if="!isMultiPropertySelection"
            label="Tools">
          <div class="media-tools media-tools--3">
            <button
                ref="transparencyButton"
                :class="{ 'is-on': popModal.isModalOpen }"
                type="button"
                class="media-tool"
                @click.stop="openTransparencyModal($event)">
              <span class="media-tool__icon"><icon name="transparency"/></span>
              <span>Opacity</span>
            </button>

            <button
                v-if="!isSVG"
                ref="adjustModalBtn"
                :class="{ 'is-on': isScaleOPen }"
                type="button"
                class="media-tool"
                @click.stop="toggleScalePop">
              <span class="media-tool__icon"><icon name="adjustments"/></span>
              <span>Adjust</span>
            </button>

            <button
                v-if="!isSVG"
                :class="{ 'is-on': $store.getters['activeProduct/getIsTrimmingEnable'] }"
                type="button"
                class="media-tool"
                @click="toggleTrim">
              <span class="media-tool__icon"><icon name="trim"/></span>
              <span>Trim video</span>
            </button>
          </div>

          <div
              v-show="popModal.isModalOpen"
              ref="transparencyModal"
              :style="getPopUpModalPositions"
              class="media-pop"
              role="dialog"
              aria-label="Transparency"
              @click.stop>
            <div class="media-pop__title">Transparency</div>
            <div class="media-pop__row">
              <label class="media-pop__label">Opacity</label>
              <input
                  :value="activeObjectForLeftPanel.effects.transparency.value"
                  :style="mediaPopTrack(activeObjectForLeftPanel.effects.transparency.value)"
                  class="media-pop__range"
                  type="range"
                  min="0"
                  max="100"
                  aria-label="Opacity"
                  @input="changeImageEffects({ name: ImageFilterTypes.OPACITY, value: parseInt($event.target.value, 10) })"
                  @mouseup="commitMediaSlider('Opacity')"
                  @touchend="commitMediaSlider('Opacity')"
                  @keyup="commitMediaSlider('Opacity')">
              <span class="media-pop__value">{{ Math.round(activeObjectForLeftPanel.effects.transparency.value) }}</span>
            </div>
          </div>

          <div
              ref="adjustModal"
              :style="[getInitialPositions, getAdjustmentModalPosition]"
              :class="[isScaleOPen ? 'show-outer-box' : 'invisible']"
              class="media-pop media-pop--adjust"
              role="dialog"
              aria-label="Adjustments"
              @click.stop>
            <div class="media-pop__title">Adjustments</div>
            <div class="media-pop__rows">
              <div
                  v-for="row in adjustEffectRows"
                  :key="row.name"
                  class="media-pop__row">
                <label class="media-pop__label">{{ row.label }}</label>
                <input
                    :value="row.value"
                    :style="mediaPopTrack(row.value)"
                    :aria-label="row.label"
                    class="media-pop__range"
                    type="range"
                    min="0"
                    max="100"
                    @input="onAdjustInput(row.name, $event.target.value)"
                    @mouseup="commitMediaSlider(row.name)"
                    @touchend="commitMediaSlider(row.name)"
                    @keyup="commitMediaSlider(row.name)">
                <label
                    v-if="row.name === 'tint'"
                    :style="{ background: tintSwatchColor }"
                    class="media-pop__swatch">
                  <input
                      :value="tintSwatchColor"
                      type="color"
                      aria-label="Tint color"
                      @input="onTintColor">
                </label>
                <span class="media-pop__value">{{ Math.round(row.value) }}</span>
              </div>
            </div>
          </div>
        </field-group>

        <field-group
            v-if="!isMultiPropertySelection"
            label="Gallery">
          <div class="media-gallery">
            <button
                v-for="(video, i) in getAlternativeVideoUrls"
                :key="i + '-alt'"
                :class="{ 'is-on': activeObjectForLeftPanel.DefaultImageUrl === video.ThumbnailVirtualPath }"
                :aria-label="'Version ' + (i + 1)"
                type="button"
                class="media-gallery__tile"
                @click.stop="replaceVideo(video)">
              <img
                  :src="'https://pdf-processing.bankmarketingcenter.com/node/image?width=44&height=44&resizeOnly=1&image=' + video.ThumbnailVirtualPath"
                  alt="">
            </button>
            <button
                type="button"
                class="media-gallery__add"
                aria-label="Add video"
                title="Add video"
                @click="toggleVideoModel">
              <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true">
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </button>
          </div>
        </field-group>
      </div>
    </div>
  </div>
</template>

<script>
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import ImageFilterTypes from '@/mixins/ImageFilterTypes';
import panelContext from '@/components/LeftSection/mixins/panelContext';
import objectAlignment from '@/components/LeftSection/mixins/objectAlignment';
import objectEffects from '@/components/LeftSection/mixins/objectEffects';
import objectTransform from '@/components/LeftSection/mixins/objectTransform';
import mediaTools from '@/components/LeftSection/mixins/mediaTools';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import PositionControls from '@/components/LeftSection/ui/PositionControls';
import RotateFlipControls from '@/components/LeftSection/ui/RotateFlipControls';

export default {
  name:       'VideoPanel',
  components: {
    'field-group':          FieldGroup,
    'position-controls':    PositionControls,
    'rotate-flip-controls': RotateFlipControls,
  },
  // objectEffects is only here for emitUndoRedoRequest (used by rotate).
  mixins: [ImageFilterTypes.Mixin, panelContext, objectAlignment, objectEffects, objectTransform, mediaTools],
  props:  {
    width:       { type: Number, default: 0 },
    height:      { type: Number, default: 0 },
    productCrop: { type: Object, default: null },
  },
  computed: {
    getAlternativeVideoUrls() {
      return this.activeObjectForLeftPanel.AlternativeVideoUrlsResVersion;
    },
  },
  methods: {
    toggleTrim() {
      this.$store.commit('activeProduct/updateISTrimmingEnable', !this.$store.getters['activeProduct/getIsTrimmingEnable']);
    },
    replaceVideo(video) {
      EventBus.$emit(ApplicationEventTypes.ImageObjectEvents.REPLACE_VIDEO, video);
    },
    toggleVideoModel() {
      if (this.$store.getters.getIsVideoModel === false) {
        this.$store.commit('setIsVideoModel', true);
      } else {
        this.$store.commit('setIsVideoModel', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';
@import '../styles/lp-media';
</style>
